class LiteYouTubePlaylist extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.videos = [];
    this.currentVideoIndex = 0;
    this.isLoading = false;
    this.sidebarScrollPosition = 0;
  }

  connectedCallback() {
    // Ensure lite-youtube is loaded before initializing
    this.waitForLiteYouTube().then(() => {
      this.parseVideoEntries();
      this.render();
      this.setupEventListeners();
    });
  }

  async waitForLiteYouTube() {
    // Wait for lite-youtube custom element to be defined
    if (!customElements.get('lite-youtube')) {
      await customElements.whenDefined('lite-youtube');
    }
  }

  parseVideoEntries() {
    const videoEntries = this.querySelectorAll('video-entry');
    this.videos = Array.from(videoEntries).map(entry => ({
      videoid: entry.getAttribute('videoid'),
      videotitle: entry.getAttribute('videotitle') || 'Untitled Video',
      videodescription: entry.getAttribute('videodescription') || 'Video description will be displayed here, but truncated so that it will only show a maximum of two lines of text. This will prevent the page elements moving during loading.',
      videoStartAt: entry.getAttribute('videoStartAt') || '0',
      params: entry.getAttribute('params') || 'rel=0',
      posterquality: entry.getAttribute('posterquality') || 'hqdefault'
    }));
  }

  get thumbLayout() {
    return this.getAttribute('thumb-layout') || 'horizontal';
  }

  get showVideoText() {
    return this.getAttribute('video-text') !== 'false';
  }

  get autoPlayThumb() {
    return this.getAttribute('auto-play-thumb') !== 'false';
  }

  saveSidebarScrollPosition() {
    const sidebar = this.shadowRoot.querySelector('.sidebar');
    if (sidebar) {
      this.sidebarScrollPosition = sidebar.scrollTop;
    }
  }

  restoreSidebarScrollPosition() {
    const sidebar = this.shadowRoot.querySelector('.sidebar');
    if (sidebar) {
      sidebar.scrollTop = this.sidebarScrollPosition;
    }
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
          max-width: 100%;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          margin-bottom: 2em;
        }

        .playlist-container {
          display: flex;
          gap: 16px;
          width: 100%;
          height: auto;
        }

        .main-viewer-section {
          flex: 1;
          min-width: 0;
          position: relative;
        }

        .main-viewer {
          position: relative;
          margin-bottom: ${this.showVideoText ? '16px' : '0'};
        }

        .main-viewer lite-youtube {
          width: 100%;
          border-radius: 12px;
          overflow: hidden;
        }

        .video-info {
          padding: 0 4px;
          min-height: ${this.showVideoText ? '80px' : '0'};
          display: ${this.showVideoText ? 'block' : 'none'};
        }

        .video-title {
          font-size: 18px;
          font-weight: bold;
          color: #333;
          margin: 0 0 8px 0;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          word-break: break-word;
        }

        .video-description {
          font-size: 14px;
          color: #666;
          margin: 0;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          word-break: break-word;
          min-height: 39px;
        }

        .sidebar {
          width: 210px;
          max-height: 500px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding-right: 8px;
          scroll-behavior: smooth;
        }

        .sidebar::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .sidebar::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .sidebar::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        .thumbnail-item {
          display: flex;
          gap: 12px;
          padding: 8px;
          border-radius: 8px;
          cursor: pointer;
          transition: background-color 0.2s ease;
          border: 2px solid transparent;
          ${this.thumbLayout === 'vertical' ? 'flex-direction: column;' : ''}
        }

        .thumbnail-item:hover {
          background-color: #f5f5f5;
        }

        .thumbnail-item.active {
          background-color: #e3f2fd;
          border-color: #2196f3;
        }

        .thumbnail-image {
          width: ${this.thumbLayout === 'vertical' ? '100%' : '80px'};
          height: ${this.thumbLayout === 'vertical' ? 'auto' : '45px'};
          ${this.thumbLayout === 'vertical' ? 'aspect-ratio: 16 / 9;' : ''}
          border-radius: 6px;
          object-fit: cover;
          flex-shrink: 0;
        }

        .thumbnail-content {
          flex: 1;
          min-width: 0;
        }

        .thumbnail-title {
          font-size: 12px;
          font-weight: 500;
          line-height: 1.3;
          color: #333;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: ${this.thumbLayout === 'vertical' ? '2' : '3'};
          -webkit-box-orient: vertical;
          overflow: hidden;
          word-break: break-word;
        }

        /* Loading placeholder styles */
        .loading-placeholder {
          background: #f0f0f0;
          border-radius: 12px;
          position: relative;
          overflow: hidden;
          aspect-ratio: 16 / 9;
        }

        .loading-content {
          position: absolute;
          bottom: 20px;
          left: 20px;
          right: 20px;
        }

        .loading-title {
          background: #d0d0d0;
          height: 20px;
          border-radius: 4px;
          margin-bottom: 12px;
          width: 70%;
        }

        .loading-description {
          background: #d0d0d0;
          height: 14px;
          border-radius: 4px;
          margin-bottom: 8px;
          width: 100%;
        }

        .loading-description:last-child {
          width: 60%;
          margin-bottom: 0;
        }

        .loading-shimmer {
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.6),
            transparent
          );
          animation: shimmer 1.5s infinite;
        }

        .loading-info-placeholder {
          margin-top: 16px;
          padding: 0 4px;
          display: ${this.showVideoText ? 'block' : 'none'};
        }

        .loading-info-title {
          background: #d0d0d0;
          height: 24px;
          border-radius: 4px;
          margin-bottom: 8px;
          width: 80%;
        }

        .loading-info-desc {
          background: #d0d0d0;
          height: 16px;
          border-radius: 4px;
          margin-bottom: 6px;
          width: 100%;
        }

        .loading-info-desc:last-child {
          width: 70%;
        }

        @keyframes shimmer {
          0% { left: -100%; }
          100% { left: 100%; }
        }

        /* Responsive design */
        @media (max-width: 768px) {
          .playlist-container {
            flex-direction: column;
          }

          .sidebar {
            width: 100%;
            max-height: 300px;
            flex-direction: row;
            overflow-x: auto;
            overflow-y: hidden;
            gap: 8px;
            padding-bottom: 8px;
          }

          .sidebar::-webkit-scrollbar {
            height: 6px;
            width: auto;
          }

          .thumbnail-item {
            flex-direction: column;
            min-width: 140px;
            flex-shrink: 0;
          }

          .thumbnail-image {
            width: 100%;
            height: 78px;
          }

          .thumbnail-title {
            font-size: 12px;
            -webkit-line-clamp: 2;
          }

          .video-title {
            font-size: 16px;
          }

          .video-description {
            font-size: 13px;
          }
        }
      </style>

      <div class="playlist-container">
        <div class="main-viewer-section">
          <div class="main-viewer">
            ${this.isLoading ? this.renderLoadingPlaceholder() : this.renderMainVideo()}
          </div>
          ${this.isLoading ? this.renderLoadingInfo() : this.renderVideoInfo()}
        </div>
        <div class="sidebar">
          ${this.renderThumbnails()}
        </div>
      </div>
    `;

    // Restore sidebar scroll position after render
    if (!this.isLoading) {
      setTimeout(() => {
        this.restoreSidebarScrollPosition();
      }, 0);
    }
  }

  renderLoadingPlaceholder() {
    return `
      <div class="loading-placeholder">
        <div class="loading-shimmer"></div>
        <div class="loading-content">
          <div class="loading-title">
            <div class="loading-shimmer"></div>
          </div>
          <div class="loading-description">
            <div class="loading-shimmer"></div>
          </div>
          <div class="loading-description">
            <div class="loading-shimmer"></div>
          </div>
        </div>
      </div>
    `;
  }

  renderLoadingInfo() {
    if (!this.showVideoText) return '';
    
    return `
      <div class="loading-info-placeholder">
        <div class="loading-info-title">
          <div class="loading-shimmer"></div>
        </div>
        <div class="loading-info-desc">
          <div class="loading-shimmer"></div>
        </div>
        <div class="loading-info-desc">
          <div class="loading-shimmer"></div>
        </div>
      </div>
    `;
  }

  renderMainVideo() {
    if (this.videos.length === 0) return '';
    
    const currentVideo = this.videos[this.currentVideoIndex];
    const existingParams = currentVideo.params;
    
    return `
      <lite-youtube
        videoid="${currentVideo.videoid}"
        videotitle="${currentVideo.videotitle}"
        videoStartAt="${currentVideo.videoStartAt}"
        params="${existingParams}"
        posterquality="${currentVideo.posterquality}"
        ${this.autoPlayThumb ? 'autoload' : ''}
      ></lite-youtube>
    `;
  }

  renderVideoInfo() {
    if (this.videos.length === 0 || !this.showVideoText) return '';
    
    const currentVideo = this.videos[this.currentVideoIndex];
    return `
      <div class="video-info">
        <h2 class="video-title">${currentVideo.videotitle}</h2>
        <p class="video-description">${currentVideo.videodescription}</p>
      </div>
    `;
  }

  renderThumbnails() {
    return this.videos.map((video, index) => `
      <div class="thumbnail-item ${index === this.currentVideoIndex ? 'active' : ''}" 
           data-index="${index}">
        <img 
          class="thumbnail-image" 
          src="https://img.youtube.com/vi/${video.videoid}/${video.posterquality}.jpg"
          alt="${video.videotitle}"
          loading="lazy"
        />
        <div class="thumbnail-content">
          <h3 class="thumbnail-title">${this.truncateTitle(video.videotitle)}</h3>
        </div>
      </div>
    `).join('');
  }

  truncateTitle(title) {
    const words = title.split(' ');
    if (words.length <= 6) return title;
    return words.slice(0, 6).join(' ') + '...';
  }

  scrollThumbnailIntoView(index) {
    const sidebar = this.shadowRoot.querySelector('.sidebar');
    const thumbnailItems = this.shadowRoot.querySelectorAll('.thumbnail-item');
    
    if (sidebar && thumbnailItems[index]) {
      const targetThumbnail = thumbnailItems[index];
      const sidebarRect = sidebar.getBoundingClientRect();
      const thumbnailRect = targetThumbnail.getBoundingClientRect();
      
      // Check if thumbnail is fully visible
      const isFullyVisible = 
        thumbnailRect.top >= sidebarRect.top && 
        thumbnailRect.bottom <= sidebarRect.bottom;
      
      if (!isFullyVisible) {
        // Calculate scroll position to center the thumbnail
        const sidebarScrollTop = sidebar.scrollTop;
        const thumbnailOffsetTop = targetThumbnail.offsetTop;
        const sidebarHeight = sidebar.clientHeight;
        const thumbnailHeight = targetThumbnail.offsetHeight;
        
        const scrollTo = thumbnailOffsetTop - (sidebarHeight / 2) + (thumbnailHeight / 2);
        sidebar.scrollTop = scrollTo;
        this.sidebarScrollPosition = scrollTo;
      }
    }
  }

  triggerAutoPlay() {
    if (this.autoPlayThumb) {
      const liteYouTube = this.shadowRoot.querySelector('lite-youtube');
      if (liteYouTube) {
        // Trigger the lite-youtube component to load and play
        setTimeout(() => {
          liteYouTube.click();
        }, 100);
      }
    }
  }

  setupEventListeners() {
    this.shadowRoot.addEventListener('click', (e) => {
      const thumbnailItem = e.target.closest('.thumbnail-item');
      if (thumbnailItem) {
        const index = parseInt(thumbnailItem.dataset.index);
        this.loadVideo(index);
      }
    });
  }

  loadVideo(index) {
    if (index === this.currentVideoIndex || index < 0 || index >= this.videos.length) {
      return;
    }

    // Save current sidebar scroll position
    this.saveSidebarScrollPosition();

    this.currentVideoIndex = index;
    this.isLoading = true;
    this.render();

    // Scroll thumbnail into view
    setTimeout(() => {
      this.scrollThumbnailIntoView(index);
    }, 100);

    // Simulate loading time to show placeholder
    setTimeout(() => {
      this.isLoading = false;
      this.render();
      this.setupEventListeners();
      
      // Trigger autoplay after the video is loaded
      if (this.autoPlayThumb) {
        setTimeout(() => {
          this.triggerAutoPlay();
        }, 100);
      }
    }, 100);
  }
}

// Define the custom element
customElements.define('lite-youtube-playlist', LiteYouTubePlaylist);

// Also define video-entry as a simple container element
class VideoEntry extends HTMLElement {
  constructor() {
    super();
  }
}

customElements.define('video-entry', VideoEntry);
